import React, { useState, useEffect } from 'react';
import { ADMIN_ROLE, ADMISSION_STAFF, ACADEMIC_COORDINATOR_STAFF } from '../utils/roles';
import {deletePolicy, fetchPolicies as fetchPoliciesAPI, fetchPolicyById, fetchProfile as fetchProfileAPI} from '../utils/api';
import { formatDateTime } from '../utils/date';
import PolicyDetailModal from './PolicyDetailModal';
import CreatePolicyModal from './CreatePolicyModal';
import EditPolicyModal from './EditPolicyModal';
import './Policies.css';

const Policies = () => {
    const [policies, setPolicies] = useState([]);
    const [total, setTotal] = useState(0);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [profile, setProfile] = useState(null);

    const fetchPolicies = async () => {
        try {
            const response = await fetchPoliciesAPI();
            setPolicies(response.data);
            setTotal(response.total);
        } catch (error) {
            console.error("Error fetching policies:", error);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                fetchPolicies();
            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        };

        loadData();
    }, []);

    const handleRowClick = async (policyId) => {
        try {
            const response = await fetchPolicyById(policyId);
            setSelectedPolicy(response.data);
        } catch (error) {
            console.error("Error fetching policy details:", error);
        }
    };

    const handleCloseModal = () => {
        setSelectedPolicy(null);
    };

    const handleOpenEditModal = () => {
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
    };

    const handleOpenCreateModal = () => {
        setCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
    };

    const handlePolicyCreated = () => {
        fetchPolicies();
    };

    const handlePolicyUpdated = () => {
        fetchPolicies();
        handleCloseEditModal();
        handleCloseModal();
    };

    const handleDeletePolicy = async () => {
        if (!selectedPolicy) return;
        try {
            await deletePolicy(selectedPolicy.id);
            fetchPolicies();
            handleCloseModal();
        } catch (error) {
            console.error("Error deleting policy:", error);
        }
    };

    const canManagePolicies = profile && profile.roles.some(role => role.id === ADMIN_ROLE || role.id === ADMISSION_STAFF || role.id === ACADEMIC_COORDINATOR_STAFF);

    return (
        <div className="students-container">
            <div className="header">
                <h1>Policies | {total}</h1>
                {canManagePolicies && (
                    <button onClick={handleOpenCreateModal} className="add-student-button">+</button>
                )}
            </div>
            <div className="policies-table-container">
                <table className="policies-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Created By</th>
                            <th>Created At</th>
                            <th>Last Updated By</th>
                            <th>Last Updated At</th>
                        </tr>
                    </thead>
                    <tbody>
                        {policies.map((policy, index) => (
                            <tr key={policy.id} onClick={() => handleRowClick(policy.id)} className="clickable-row">
                                <td>{index + 1}</td>
                                <td>{policy.name}</td>
                                <td>{policy.created_by && `${policy.created_by.first_name} ${policy.created_by.last_name}`}</td>
                                <td>{formatDateTime(policy.created_at)}</td>
                                <td>{policy.last_updated_by && `${policy.last_updated_by.first_name} ${policy.last_updated_by.last_name}`}</td>
                                <td>{formatDateTime(policy.last_updated_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {selectedPolicy && (
                <PolicyDetailModal
                    policy={selectedPolicy}
                    onClose={handleCloseModal}
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeletePolicy}
                    canManage={profile && (profile.roles.some(role => role.id === ADMIN_ROLE) || (selectedPolicy.created_by && selectedPolicy.created_by.id === profile.id))}
                />
            )}
            {isCreateModalOpen && (
                <CreatePolicyModal
                    onClose={handleCloseCreateModal}
                    onPolicyCreated={handlePolicyCreated}
                />
            )}
            {isEditModalOpen && (
                <EditPolicyModal
                    policy={selectedPolicy}
                    onClose={handleCloseEditModal}
                    onPolicyUpdated={handlePolicyUpdated}
                />
            )}
        </div>
    );
};

export default Policies;